import { Alert, Button, Input, Select, Spin, Switch } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Organization } from '../../reducers/organizationReducer'
import { User } from '../../reducers/userReducer'
import { useAxiosWithHeader } from '../../utils'
import './reminder-setup.css'

export const DEFAULT_CRON = '0 9-17/3 * * 1-5'
const PRESETS = [
  { value: DEFAULT_CRON, label: 'Weekdays at 09:00, 12:00 and 15:00' },
  { value: '0 9 * * 1-5', label: 'Weekdays at 09:00' },
  { value: '0 9,15 * * 1-5', label: 'Weekdays at 09:00 and 15:00' },
  { value: '0 */3 * * *', label: 'Every 3 hours, including nights and weekends' },
]

function errorText(error) {
  const data = error?.response?.data
  if (typeof data === 'string') return data
  if (typeof data?.message === 'string') return data.message
  return (
    data?.message?.[0]?.messages?.[0]?.message ||
    'Could not update reminders. Please try again.'
  )
}

function initialForm(data, scope) {
  const schedule =
    scope === 'personal'
      ? data.personalSchedule || data.organizationSchedule
      : data.organizationSchedule
  return {
    cron: schedule?.cron || DEFAULT_CRON,
    enabled: Boolean(schedule?.enabled),
    maxReminders: schedule?.maxReminders ?? 0,
    inactivityHours: schedule?.inactivityHours || 12,
    inherit: scope === 'personal' && !data.personalSchedule,
    allowPersonalReminders: data.allowPersonalReminders,
  }
}

export default function ReminderSetup({ isAdmin }) {
  const organization = useSelector(Organization.selectors.getOrganization)
  const user = useSelector(User.selectors.selectUser)
  const dispatch = useDispatch()
  const axios = useAxiosWithHeader()
  const api = useRef(axios)
  api.current = axios
  const settingId =
    typeof organization.setting === 'string'
      ? organization.setting
      : organization.setting?.id || organization.setting?._id
  const context = `${settingId}:${user.id}`
  const currentContext = useRef(context)
  currentContext.current = context
  const [data, setData] = useState(null)
  const [scope, setScope] = useState(isAdmin ? 'organization' : 'personal')
  const [form, setForm] = useState(null)
  const [baseline, setBaseline] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [reload, setReload] = useState(0)
  const [preview, setPreview] = useState(null)
  const [previewError, setPreviewError] = useState('')
  const [checking, setChecking] = useState(false)
  const requestId = useRef(0)
  const pull = organization.provider === 'gitlab' ? 'MR' : 'PR'
  const endpoint = `/settings/${settingId}/reminder-schedule`
  const cron = form?.cron

  useEffect(() => {
    let cancelled = false
    setData(null)
    setForm(null)
    setLoading(true)
    setSaving(false)
    setError('')
    setSaved(false)
    if (!settingId)
      return () => {
        cancelled = true
      }
    api.current
      .get(endpoint)
      .then(({ data: response }) => {
        if (cancelled) return
        const nextScope =
          isAdmin && response.canManageOrganization ? 'organization' : 'personal'
        const nextForm = initialForm(response, nextScope)
        setData(response)
        setScope(nextScope)
        setForm(nextForm)
        setBaseline(JSON.stringify(nextForm))
      })
      .catch((e) => {
        if (!cancelled) setError(errorText(e))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [settingId, context, endpoint, isAdmin, reload])

  useEffect(() => {
    const id = ++requestId.current
    setPreview(null)
    setPreviewError('')
    if (cron === undefined) return
    setChecking(true)
    const timer = setTimeout(() => {
      api.current
        .post(`${endpoint}/preview`, { cron })
        .then(({ data: result }) => {
          if (id === requestId.current) setPreview(result)
        })
        .catch((e) => {
          if (id === requestId.current) setPreviewError(errorText(e))
        })
        .finally(() => {
          if (id === requestId.current) setChecking(false)
        })
    }, 300)
    return () => {
      clearTimeout(timer)
      requestId.current += 1
    }
  }, [cron, data?.timeZone, endpoint])

  function change(key, value) {
    setForm((previous) =>
      key === 'inherit' && value
        ? { ...previous, ...initialForm({ ...data, personalSchedule: null }, 'personal') }
        : { ...previous, [key]: value },
    )
    setError('')
    setSaved(false)
  }

  function changeScope(nextScope) {
    const nextForm = initialForm(data, nextScope)
    setScope(nextScope)
    setForm(nextForm)
    setBaseline(JSON.stringify(nextForm))
    setSaved(false)
    setError('')
  }

  async function save() {
    setSaving(true)
    setError('')
    setSaved(false)
    const requestContext = context
    try {
      const payload = {
        scope,
        schedule: form.inherit
          ? null
          : {
              cron: form.cron,
              enabled: form.enabled,
              maxReminders: form.maxReminders,
              inactivityHours: form.inactivityHours,
            },
      }
      if (scope === 'organization')
        payload.allowPersonalReminders = form.allowPersonalReminders
      const { data: response } = await api.current.put(endpoint, payload)
      if (currentContext.current !== requestContext) return
      const nextForm = initialForm(response, scope)
      setData(response)
      setForm(nextForm)
      setBaseline(JSON.stringify(nextForm))
      setSaved(true)
      if (scope === 'organization')
        dispatch(
          Organization.actions.setData({
            setting: {
              ...organization.setting,
              reminders: response.organizationEnabled,
              reminderSchedule: response.organizationSchedule,
              reminderInactivity: response.organizationSchedule.inactivityHours,
              allowPersonalReminders: response.allowPersonalReminders,
            },
          }),
        )
    } catch (e) {
      if (currentContext.current === requestContext) setError(errorText(e))
    } finally {
      if (currentContext.current === requestContext) setSaving(false)
    }
  }

  if (loading)
    return (
      <section className="axolo-reminder-setup" aria-label="Loading reminders">
        <Spin size="small" /> Loading reminders…
      </section>
    )
  if (!data || !form)
    return (
      <section className="axolo-reminder-setup">
        <Alert type="error" message={error || 'Could not load reminders.'} />
        <Button onClick={() => setReload((value) => value + 1)}>Try again</Button>
      </section>
    )

  const personalAllowed = data.canManagePersonal && data.allowPersonalReminders
  const canEdit = scope === 'organization' ? data.canManageOrganization : personalAllowed
  const disabled = saving || !canEdit || form.inherit
  const dirty = JSON.stringify(form) !== baseline
  const canSave =
    scope === 'personal' && form.inherit
      ? data.canManagePersonal
      : canEdit && !!preview && !checking && !previewError
  const limits = [...new Set([0, 3, 5, 10, form.maxReminders])].sort((a, b) => a - b)
  const thresholds = [
    ...new Set([3, 6, 12, 24, 36, 48, 72, 168, form.inactivityHours]),
  ].sort((a, b) => a - b)
  const usingDailyReminders =
    data.organizationSchedule.legacy &&
    (scope === 'organization' || form.inherit || !personalAllowed)
  const switchingFromDaily = scope === 'organization' && data.organizationSchedule.legacy
  const scheduleDescription =
    PRESETS.find((preset) => preset.value === preview?.cron)?.label ||
    preview?.description

  return (
    <section className="axolo-reminder-setup" aria-label={`${pull} reminders`}>
      <div className="reminder-heading">
        <h1 className="text-xl font-semibold text-gray-900">
          {scope === 'personal' ? `My ${pull}` : pull} reminders
        </h1>
        <div className="reminder-toggle">
          <Switch
            aria-label="Enable reminders"
            checked={form.enabled}
            disabled={disabled}
            onChange={(value) => change('enabled', value)}
          />
          <span>
            {form.inherit ? 'Organization default' : form.enabled ? 'Enabled' : 'Paused'}
          </span>
        </div>
      </div>
      <p className="reminder-intro">
        Reminders are posted in each {pull}’s Slack channel, using your existing reminder
        messages.
      </p>
      <div className="reminder-fields">
        <div>
          <div className="reminder-label">
            <label htmlFor="reminder-cron">Cron schedule</label>
            <Select
              aria-label="Schedule presets"
              placeholder="Examples"
              value={null}
              bordered={false}
              disabled={disabled}
              onChange={(value) => change('cron', value)}
              options={PRESETS}
              dropdownMatchSelectWidth={false}
            />
          </div>
          <Input
            id="reminder-cron"
            value={form.cron}
            spellCheck={false}
            autoComplete="off"
            disabled={disabled}
            status={previewError ? 'error' : undefined}
            aria-invalid={Boolean(previewError)}
            aria-describedby="reminder-description"
            onChange={(event) => change('cron', event.target.value)}
          />
        </div>
        <div>
          <div className="reminder-label">
            <label htmlFor="reminder-limit">Max per {pull}</label>
          </div>
          <Select
            id="reminder-limit"
            aria-label={`Max per ${pull}`}
            value={form.maxReminders}
            disabled={disabled}
            onChange={(value) => change('maxReminders', value)}
            options={limits.map((value) => ({
              value,
              label: value === 0 ? 'No limit' : `${value} reminders`,
            }))}
          />
        </div>
        <Button
          type="primary"
          onClick={save}
          loading={saving}
          disabled={(!dirty && !switchingFromDaily) || !canSave}
        >
          {switchingFromDaily ? 'Switch to cron' : 'Save'}
        </Button>
      </div>
      <p
        id="reminder-description"
        className={previewError ? 'reminder-error' : 'reminder-description'}
        role={previewError ? 'alert' : 'status'}
      >
        {previewError ||
          (checking
            ? 'Checking schedule…'
            : preview
            ? `${scheduleDescription} · ${preview.timeZone}`
            : '')}
      </p>
      {switchingFromDaily && (
        <p className="reminder-migration">
          {data.organizationEnabled
            ? 'Your daily reminders are still active.'
            : 'Your daily reminders are currently paused.'}{' '}
          Saving replaces them with reminders for pending reviews only.
        </p>
      )}
      {error && (
        <p className="reminder-error" role="alert">
          {error}
        </p>
      )}
      {scope === 'personal' && !data.allowPersonalReminders && (
        <p className="reminder-description">
          Your admin needs to allow personal reminder schedules.
        </p>
      )}
      {scope === 'personal' && !data.organizationEnabled && (
        <p className="reminder-description">
          Organization reminders are paused. Your schedule will start when an admin
          enables them.
        </p>
      )}
      <details className="reminder-advanced">
        <summary>Advanced</summary>
        <div className="reminder-advanced-fields">
          {data.canManageOrganization && data.canManagePersonal && (
            <label>
              Applies to
              <Select
                aria-label="Applies to"
                value={scope}
                disabled={saving}
                onChange={changeScope}
                options={[
                  { value: 'organization', label: `Organization ${pull}s` },
                  { value: 'personal', label: `My ${pull}s` },
                ]}
              />
            </label>
          )}
          {scope === 'personal' && (
            <label>
              Schedule source
              <Select
                aria-label="Schedule source"
                disabled={saving || !data.canManagePersonal}
                value={form.inherit ? 'organization' : 'custom'}
                onChange={(value) => change('inherit', value === 'organization')}
                options={[
                  { value: 'organization', label: 'Use organization schedule' },
                  {
                    value: 'custom',
                    label: 'Custom schedule',
                    disabled: !personalAllowed,
                  },
                ]}
              />
            </label>
          )}
          <label>
            Wait for no review activity for
            <Select
              aria-label="Inactivity threshold"
              value={form.inactivityHours}
              disabled={disabled}
              onChange={(value) => change('inactivityHours', value)}
              options={thresholds.map((value) => ({ value, label: `${value} hours` }))}
            />
          </label>
          {scope === 'organization' && (
            <label>
              Allow personal schedules
              <Switch
                aria-label="Allow personal schedules"
                checked={form.allowPersonalReminders}
                disabled={saving}
                onChange={(value) => change('allowPersonalReminders', value)}
              />
            </label>
          )}
          <p>
            At each scheduled time, mention reviewers who still need to review, after at
            least {form.inactivityHours} hours without review activity. The limit resets
            when a new review is requested. Reviewed or closed {pull}s stop reminders;
            drafts and Slack snooze pause them.
          </p>
          <p>
            Your saved message templates are kept. This schedule uses the messages for
            reviewable {pull}s; it does not send draft, work-in-progress or ready-to-merge
            reminders.
          </p>
          <p>
            Five fields: minute, hour, day of month, month, weekday. Use one minute value;
            reminders can run at most hourly. Times use the organization’s time zone.
          </p>
          {preview?.nextRuns && (
            <p>
              Next scheduled times:{' '}
              {preview.nextRuns
                .map((value) =>
                  new Intl.DateTimeFormat('en-GB', {
                    timeZone: preview.timeZone,
                    weekday: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  }).format(new Date(value)),
                )
                .join(' · ')}
            </p>
          )}
          {scope === 'personal' && !form.inherit && (
            <p>
              This replaces organization review reminders for your {pull}s. Pausing it
              does not switch back to the organization schedule. Choose “Use organization
              schedule” to restore the default.
            </p>
          )}
        </div>
      </details>
      <p className="reminder-status" role="status">
        {saved
          ? 'Schedule saved.'
          : dirty
          ? 'Unsaved changes — reminders keep their current settings until you save.'
          : usingDailyReminders
          ? `Using existing daily reminders · ${
              data.organizationEnabled ? 'Active' : 'Paused'
            }`
          : form.inherit
          ? 'Using organization reminders'
          : !form.enabled
          ? 'This schedule is paused'
          : form.maxReminders
          ? `Up to ${form.maxReminders} reminders per ${pull}, until reviewed`
          : 'No limit · Stops when the review is complete'}
      </p>
    </section>
  )
}
